<template>
  <div class="tasks">
    <v-card class="ma-1">
      <v-row class="mx-0">
        <v-col cols="2">
          <v-select
            :items="dateRangeList"
            :value="dateRangeList[0]"
            :disabled="isLoading"
            label="Period"
            @change="changeTimePeriod"
            hide-details
            class="report-base-filter"
            style="min-width: 150px"
          />
        </v-col>
        <v-col cols="2">
          <date-picker
            v-model="filters.from"
            label="Date From"
            :disabled="isLoading"
          />
        </v-col>
        <v-col cols="2">
          <date-picker
            v-model="filters.to"
            label="Date To"
            :disabled="isLoading"
          />
        </v-col>
        <v-col
          class="d-flex align-center"
          cols="3"
        >
          <v-btn
            v-text="'Search'"
            class="mt-2"
            color="info"
            :loading="isLoading"
            @click="$emit('search', filters)"
          />
          <v-btn
            icon
            class="mt-2 ml-2"
            color="primary"
            @click="isAnalyticsDisplayed = true"
          >
            <v-icon>mdi-chart-box-outline</v-icon>
          </v-btn>
          <export
            class="mb-0"
            :fields="exportFields"
            without_sg
            :data="tasks"
          />
        </v-col>
      </v-row>

      <v-row
        v-if="!selectedTasks.length"
        class="mx-0"
      >
        <v-col cols="1">
          <v-select
            v-model="filters.priority"
            :items="Object.entries(PRIORITY_OPTIONS)"
            :disabled="isLoading"
            item-value="0"
            item-text="1"
            hide-details
            clearable
            multiple
            label="Priority"
          />
        </v-col>
        <v-col cols="1">
          <v-select
            v-model="filters.status"
            :items="Object.entries(STATUS_OPTIONS)"
            :disabled="isLoading"
            item-value="0"
            item-text="1"
            hide-details
            clearable
            multiple
            label="Status"
          />
        </v-col>
        <v-col cols="1">
          <v-select
            v-model="filters.type"
            :items="Object.entries(TYPE_OPTIONS)"
            :disabled="isLoading"
            item-value="0"
            item-text="1"
            hide-details
            clearable
            multiple
            label="Type"
          />
        </v-col>
        <v-col cols="1">
          <v-text-field
            v-model="filters.player"
            clearable
            hide-details
            :disabled="isLoading"
            label="Player"
            placeholder="Player ID or login"
          />
        </v-col>
        <v-col
          v-if="operatorOptions.length"
          cols="1"
        >
          <v-autocomplete
            v-model="filters.assignee"
            :items="operatorOptions"
            :disabled="isLoading"
            clearable
            hide-details
            item-value="0"
            item-text="1"
            label="Assignee"
          />
        </v-col>
        <v-col
          v-if="operatorOptions.length"
          cols="1"
        >
          <v-autocomplete
            v-model="filters.performer"
            :items="operatorOptions"
            :disabled="isLoading"
            clearable
            hide-details
            item-value="0"
            item-text="1"
            label="Performer"
          />
        </v-col>
        <v-col cols="2">
          <v-select
            v-model="filters.tags"
            :items="tagSuggestions"
            multiple
            label="Tags"
            hide-details
          />
        </v-col>
        <v-col
          cols="1"
          class="d-flex align-end"
        >
          <v-btn
            color="primary"
            :loading="isLoading"
            @click="onAddNewTask"
          >
            <v-icon>
              mdi-plus
            </v-icon>
            New
          </v-btn>
        </v-col>
      </v-row>

      <v-row
        v-if="selectedTasks.length"
        class="mx-0"
      >
        <v-col cols="2">
          <v-select
            v-model="bulkUpdateParams.priority"
            :items="Object.entries(PRIORITY_OPTIONS)"
            item-text="1"
            item-value="0"
            label="Priority"
            clearable
            hide-details
          />
        </v-col>
        <v-col cols="2">
          <v-select
            v-model="bulkUpdateParams.status"
            :items="Object.entries(STATUS_OPTIONS)"
            item-text="1"
            item-value="0"
            label="Status"
            clearable
            hide-details
          />
        </v-col>
        <v-col cols="1">
          <v-select
            v-model="bulkUpdateParams.type"
            :items="Object.entries(TYPE_OPTIONS)"
            item-text="1"
            item-value="0"
            label="Type"
            clearable
            hide-details
          />
        </v-col>
        <v-col cols="2">
          <v-select
            v-if="canAssignToOtherPeople"
            v-model="bulkUpdateParams.assignee"
            :items="operatorOptions"
            label="Assignee"
            item-value="0"
            item-text="1"
            clearable
            hide-details
          />
        </v-col>
        <v-col
          class="ml-auto d-flex justify-end align-center"
          cols="3"
        >
          <v-btn
            outlined
            color="primary"
            :loading="isLoading"
            @click="onBulkUpdate"
          >
            <v-icon>
              mdi-play-circle-outline
            </v-icon>
            Apply
          </v-btn>

          <v-btn
            class="ml-3"
            outlined
            color="warning"
            :loading="isLoading"
            @click="selectedTasks = []"
          >
            <v-icon>
              mdi-close-box-outline
            </v-icon>
            Cancel
          </v-btn>

          <span class="primary--text ml-3 font-weight-medium">
            {{ selectedTasks.length }} selected
          </span>
        </v-col>
      </v-row>

      <v-data-table
        v-model="selectedTasks"
        :headers="canEditTasks ? headers : headers.slice(0, -1)"
        :items="tasks"
        item-key="task_id"
        :loading="isLoading"
        sort-desc
        :show-select="canEditTasks"
        height="calc(100vh - 145px)"
      >
        <template #item.player_login="{value}">
          <a :href="'/user-management/' + value" target="_blank">{{ value }}</a>
        </template>
        <template #item.body="{value}">
          <div v-html="$sanitize(value)" />
        </template>
        <template #item.author_id="{value}">
          <span
            v-if="operatorsById"
            :class="{ 'text--disabled': operatorsById[value]?.is_active !== 1 }"
            v-text="operatorsById[value]?.login || ' - '"
          />
          <v-progress-circular v-else />
        </template>
        <template #item.user_id="{value}">
          <span
            v-if="operatorsById"
            :class="{ 'text--disabled': operatorsById[value]?.is_active !== 1 }"
            v-text="operatorsById[value]?.login || ' - '"
          />
          <v-progress-circular v-else />
        </template>
        <template #item.performer_id="{value}">
          <span
            v-if="operatorsById"
            :class="{ 'text--disabled': operatorsById[value]?.is_active !== 1 }"
            v-text="operatorsById[value]?.login || ' - '"
          />
          <v-progress-circular v-else />
        </template>

        <template #item.type="{value}">
          {{ TYPE_OPTIONS[value] }}
        </template>
        <template #item.priority="{value, item}">
          <v-select
            v-if="canEditTasks"
            :value="value"
            :items="Object.entries(PRIORITY_OPTIONS)"
            item-value="0"
            item-text="1"
            hide-details
            class="mt-0 pt-0"
            dense
            style="font-size: 11pt; max-width: 120px;"
            @change="$emit('inline-edit', {...item, priority: $event}, filters)"
          />
          <template v-else>
            {{ PRIORITY_OPTIONS[value] }}
          </template>
        </template>
        <template #item.status="{value, item}">
          <v-select
            v-if="canEditTasks"
            :value="value"
            :items="Object.entries(STATUS_OPTIONS)"
            item-value="0"
            item-text="1"
            hide-details
            class="mt-0 pt-0"
            dense
            style="font-size: 11pt; max-width: 120px;"
            @change="$emit('inline-edit', {...item, status: $event}, filters)"
          />
          <template v-else>
            {{ STATUS_OPTIONS[value] }}
          </template>
        </template>

        <template #item.tags="{value}">
          <template
            v-for="(tag, index) in value"
          >
            <v-chip
              v-if="index < MAX_TAGS"
              v-text="tag"
              class="ma-1"
              :key="tag"
              color="primary"
            />
            <v-chip
              v-if="index === MAX_TAGS"
              class="ma-1"
              v-text="`+ ${value.length - MAX_TAGS} more`"
              outlined
            />
          </template>
        </template>

        <template #item.actions="{item}">
          <div class="tasks-actions">
            <v-icon
              :disabled="isLoading"
              dense
              @click="taskToEdit = item, dialog = true"
            >
              edit
            </v-icon>
            <v-icon
              :disabled="isLoading"
              dense
              @click="$emit('delete', item.task_id, filters)"
            >
              delete
            </v-icon>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <v-dialog
      v-model="dialog"
      max-width="900"
    >
      <task-form
        v-if="taskToEdit"
        :task="taskToEdit"
        :operators-by-id="operatorsById"
        :operator-id="operatorId"
        :tag-suggestions="tagSuggestions"
        :can-assign-to-other-people="canAssignToOtherPeople"
        :is-loading="isLoading"
        @close="dialog = false, taskToEdit = null"
        @save="$emit('save', $event, filters)"
      />
    </v-dialog>

    <v-dialog
      v-model="isAnalyticsDisplayed"
      max-width="900"
    >
      <v-card
        :loading="isLoading"
      >
        <v-card-title>
          Task Analytics
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="4">
              <v-select
                :items="dateRangeList"
                :value="dateRangeList[0]"
                :disabled="isLoading"
                label="Period"
                @change="changeAnalyticsTimePeriod"
                hide-details
                class="report-base-filter"
                style="min-width: 150px"
              />
            </v-col>
            <v-col cols="4">
              <date-picker
                v-model="analyticsFrom"
                label="Date From"
                :disabled="isLoading"
              />
            </v-col>
            <v-col cols="4">
              <date-picker
                v-model="analyticsTo"
                label="Date To"
                :disabled="isLoading"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="4">
              <v-autocomplete
                v-model="analyticsGroupBy"
                label="Analytics By"
                :items="ANALYTICS_GROUP_BY_OPTIONS"
                :loading="isLoading"
                multiple
                clearable
                hide-details
              />
            </v-col>
            <v-col
              cols="2"
              class="ml-auto"
            >
              <v-btn
                color="primary"
                :loading="isLoading"
                :disabled="!analyticsGroupBy.length || !analyticsFrom || !analyticsTo"
                @click="showAnalytics"
              >
                Search
              </v-btn>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12">
              <v-data-table
                :headers="analyticsTableColumns"
                :loading="isLoading"
                :items="analytics"
              />
            </v-col>
          </v-row>
        </v-card-text>

        <v-card-actions>
          <v-btn
            color="error"
            @click="isAnalyticsDisplayed = false"
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
  import { STATUS_OPTIONS, TYPE_OPTIONS, ANALYTICS_GROUP_BY_OPTIONS, getEmptyTask, PRIORITY_OPTIONS } from '../consts'
  import TaskForm from './TaskForm.vue'
  import { getOperatorName } from '../utils'
  import DatePicker from '@/components/core/DatePicker/DatePicker.vue'

  export default {
    components: {
      TaskForm,
      DatePicker,
      Export: () => import('@/components/helper/Export'),
    },
    props: {
      tasks: {
        type: Array,
        default: () => ([])
      },
      operatorsById: {
        type: Object,
        default: () => ({})
      },
      tagSuggestions: {
        type: Array,
        required: true,
      },
      analytics: {
        type: Array,
        required: true,
      },
      canAssignToOtherPeople: Boolean,
      canEditTasks: Boolean,
      operatorId: Number,
      isLoading: Boolean,
    },
    data () {
      return {
        headers: [
          { text: 'Date', value: 'date' },
          { text: 'Player', value: 'player_login' },
          { text: 'Body', value: 'body', sortable: false },
          { text: 'Author', value: 'author_id', sortable: false },
          { text: 'Assignee', value: 'user_id', sortable: false },
          { text: 'Performer', value: 'performer_id', sortable: false },
          { text: 'Priority', align: 'left', value: 'priority' },
          { text: 'Type', value: 'type', sortable: false },
          { text: 'Status', align: 'left', sortable: false, value: 'status' },
          { text: 'Tags', align: 'left', sortable: false, value: 'tags', width: '15%' },
          { text: 'Done At', align: 'left', sortable: false, value: 'done', },
          { text: 'Created', align: 'left', sortable: false, value: 'created', },
          { text: 'Actions', value: 'actions', sortable: false, width: '75px' }
        ],
        taskToEdit: null,
        dialog: false,
        PRIORITY_OPTIONS,
        STATUS_OPTIONS,
        TYPE_OPTIONS,
        filters: {
          assignee: null,
          player: null,
          performer: null,
          status: ['new'],
          type: [],
          priority: [],
          tags: [],
          from: dateRangeList[0].value.from,
          to: dateRangeList[0].value.to
        },
        dateRangeList,
        selectedTasks: [],
        bulkUpdateParams: {
          priority: '',
          assignee: '',
          status: '',
          type: '',
        },
        MAX_TAGS: 3,

        isAnalyticsDisplayed: false,
        analyticsGroupBy: [],
        analyticsFrom: dateRangeList[0].value.from,
        analyticsTo: dateRangeList[0].value.to,
        ANALYTICS_GROUP_BY_OPTIONS,
      }
    },

    computed: {
      operatorOptions () {
        if (!this.operatorsById) {
          return []
        }

        return Object.keys(this.operatorsById).map(id => [ id, getOperatorName(this.operatorsById[id]) ])
      },

      analyticsTableColumns() {
        return [
          ...this.ANALYTICS_GROUP_BY_OPTIONS,
          { text: 'Total', value: 'total' },
        ]
      },

      exportFields() {
        return this.headers.map(({value}) => value).filter(key => key !== 'actions')
      },
    },

    watch: {
      dialog (val) {
        if (!val) {
          setTimeout(() => {
            this.taskToEdit = null
          }, 300)
        }
      },
      isLoading (val) {
        if (!val && this.dialog) {
          this.close()
        }

        if (!val && this.selectedTasks.length) {
          this.selectedTasks = []
          this.bulkUpdateParams = {
            priority: '',
            assignee: '',
            status: '',
            type: '',
          }
        }
      }
    },
    methods: {
      close () {
        this.dialog = false
        this.taskToEdit = null
      },

      changeTimePeriod (value) {
        this.filters.from = value.from
        this.filters.to = value.to
      },

      changeAnalyticsTimePeriod (value) {
        this.analyticsFrom = value.from
        this.analyticsTo = value.to
      },

      showAnalytics() {
        this.$emit('show-analytics', {
          from: this.analyticsFrom,
          to: this.analyticsTo,
          group_by: this.analyticsGroupBy,
        })
      },

      onAddNewTask() {
        this.taskToEdit = getEmptyTask()
        this.dialog = true
      },

      onBulkUpdate() {
        this.$emit('bulk-update', {
          task_ids: this.selectedTasks.map(task => task.task_id),
          ...this.bulkUpdateParams,
        })
      }
    }
  }
</script>

<style lang="scss" scoped>
  .tasks {
    &-actions {
      display: flex;
      gap: 0.5rem;
    }
  }
</style>
